import type { ReactNode } from 'react';

import { useTranslation } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { cn } from '@nucleus-commerce/ui/lib/utils';

export type EditModalSize = 'sm' | 'md' | 'lg' | 'xl';

const WIDTHS: Record<EditModalSize, string> = {
  sm: 'sm:max-w-xl',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-3xl',
  xl: 'sm:max-w-4xl',
};

export interface EditModalProps {
  open: boolean;
  /** Closing by the X, Escape or a click outside counts as Cancel. */
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Pick by what's inside: the body scrolls, the header and footer stay. */
  size?: EditModalSize;
  children: ReactNode;
  /** Left of the buttons: a count, a link, a Remove. */
  footerStart?: ReactNode;
  /** Replaces Cancel and Done, for an action modal whose button acts at once. */
  footer?: ReactNode;
  onDone?: () => void;
  doneLabel?: string;
  doneDisabled?: boolean;
  /** `data-tour` value when a guided tour points at this modal. */
  tour?: string;
  className?: string;
}

/**
 * The one modal of the product pages: a fixed header and footer around a scrolling body. An edit
 * modal changes one section of the page, Done keeps the change on the page and the save bar saves
 * it (see `useEditModal`). An action modal passes its own `footer` and acts at once.
 */
export function EditModal({
  open,
  onOpenChange,
  title,
  description,
  size = 'sm',
  children,
  footerStart,
  footer,
  onDone,
  doneLabel,
  doneDisabled = false,
  tour,
  className,
}: EditModalProps) {
  const { t } = useTranslation('common');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-tour={tour}
        className={cn(
          'flex max-h-[calc(100dvh-4rem)] flex-col gap-0 overflow-hidden p-0',
          WIDTHS[size],
          className,
        )}
      >
        <DialogHeader className="px-6 pt-5 pb-3 text-left">
          <DialogTitle>{title}</DialogTitle>
          {/* Radix warns without a description; an empty one keeps the dialog labelled. */}
          <DialogDescription className={description ? undefined : 'sr-only'}>
            {description ?? title}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 pt-1 pb-5">
          {children}
        </div>
        <div className="bg-muted/40 flex flex-wrap items-center justify-end gap-2 border-t px-6 py-3">
          {footerStart ? <div className="mr-auto min-w-0 text-sm">{footerStart}</div> : null}
          {footer ?? (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('actions.cancel')}
              </Button>
              <Button type="button" disabled={doneDisabled} onClick={onDone}>
                {doneLabel ?? t('actions.done')}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
